import React, { ComponentProps, useContext, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

// components
import SalonsAndCitiesSearch, { GET_MY_LOCATION_OPTION } from '../SalonsAndCitiesSearch/SalonsAndCitiesSearch'

// types
import {
	SalonsAndCitiesSearchValue,
	LoadSalonsFromFiltersParams,
	SalonsCategoryPageQuery,
	SalonsListPageQuery,
	CitiesSeoSlugResponse,
	CityByPlaceId,
	CmsCitiesByCategoryData
} from '../../types/types'

// styles
import * as SC from './SalonsAndCitiesSearchWrapStyles'
import { SALONS_PAGE_MOBILE_BREAKPOINT_INT } from '../../styles/constants'

// utils
import { SEARCH_FIELD_OPTION_TYPE } from '../../utils/enums'
import { AppContext } from '../../utils/appProvider'
import { MyLocationContext } from '../../utils/myLocationProvider'
import { getPlaceSelectedEvent, getSalonSelectedFromSearchEvent, getSearchFocusedEvent } from '../../utils/dataLayerEvents'
import { pushToDataLayer } from '../../utils/dataLayer'
import { PAGE_LINKS } from '../../utils/helper'

// hooks
import useMessages from '../../hooks/useMessages'
import useIsMobile from '../../hooks/useIsMobile'

type Props = {
	query: SalonsCategoryPageQuery | SalonsListPageQuery
	loadSalonsFromFilters: (params: LoadSalonsFromFiltersParams) => void
	currentCategory?: {
		slug: string
		id: string
	}
	cityByPlaceIdData?: CityByPlaceId
	citiesSeoSlugData?: CitiesSeoSlugResponse
}

// search for both salons and cities
const SalonsAndCitiesSearchWrap = (props: Props) => {
	const { query, loadSalonsFromFilters, currentCategory, citiesSeoSlugData, cityByPlaceIdData } = props
	const { messages } = useMessages()
	const { locale } = useIntl()

	const { apiBrowser, countryCode } = useContext(AppContext)
	const { myLocation, isLoadingMyLocation } = useContext(MyLocationContext)

	const [searchValue, setSearchValue] = useState<SalonsAndCitiesSearchValue>(null)
	const [isLoadingSearchValue, setIsLoadingSearchValue] = useState(true)
	const [citiesByCategory, setCitiesByCategory] = useState<CmsCitiesByCategoryData | null>(null)
	const [isLoadingCitiesByCategory, setIsLoadingCitiesByCategory] = useState(false)
	const searchValueType = searchValue?.extra?.type

	const isLocationInitializedRef = useRef(false)

	const isMobile = useIsMobile(SALONS_PAGE_MOBILE_BREAKPOINT_INT)

	useEffect(() => {
		;(async () => {
			if (currentCategory?.slug) {
				try {
					setIsLoadingCitiesByCategory(true)
					const result = await apiBrowser.cms.getCitiesByCategory({
						countryCode,
						categorySlug: currentCategory.slug
					})
					setCitiesByCategory(result)
				} catch {
					setCitiesByCategory(null)
				} finally {
					setIsLoadingCitiesByCategory(false)
				}
			}
		})()
	}, [currentCategory?.slug, countryCode, apiBrowser])

	useEffect(() => {
		// Initialize search value
		;(async () => {
			if (isLoadingMyLocation) {
				return
			}
			if ((searchValueType && searchValueType === SEARCH_FIELD_OPTION_TYPE.SALON) || isLocationInitializedRef.current) {
				setIsLoadingSearchValue(false)
				return
			}

			let newSearchValue: SalonsAndCitiesSearchValue = null

			if (query.googlePlaceID && typeof query.lat === 'number' && typeof query.lon === 'number') {
				let cityName = searchValue?.label
				if (!cityName) {
					const city = await apiBrowser.b2c.getCityNameByPlaceId(query.googlePlaceID)
					cityName = city?.name || query.googlePlaceID
				}

				newSearchValue = {
					key: query.googlePlaceID,
					value: query.googlePlaceID,
					label: cityName,
					extra: { type: SEARCH_FIELD_OPTION_TYPE.CITY, longitude: query.lon, latitude: query.lat }
				}
			} else if (cityByPlaceIdData?.name) {
				newSearchValue = {
					key: cityByPlaceIdData.placeID,
					value: cityByPlaceIdData.placeID,
					label: cityByPlaceIdData.name,
					extra: { type: SEARCH_FIELD_OPTION_TYPE.CITY, longitude: cityByPlaceIdData.lon, latitude: cityByPlaceIdData.lat }
				}
			} else if (myLocation?.latMy && myLocation.lonMy) {
				newSearchValue = GET_MY_LOCATION_OPTION({ latitude: myLocation.latMy, longitude: myLocation.lonMy })
			}
			isLocationInitializedRef.current = true
			setSearchValue(newSearchValue)
			setIsLoadingSearchValue(false)
		})()
	}, [
		isLoadingMyLocation,
		apiBrowser,
		searchValue?.label,
		searchValueType,
		query.googlePlaceID,
		query.lat,
		query.lon,
		myLocation?.latMy,
		myLocation?.lonMy,
		cityByPlaceIdData?.placeID,
		cityByPlaceIdData?.lon,
		cityByPlaceIdData?.lat,
		cityByPlaceIdData?.name
	])

	const handleChange: ComponentProps<typeof SalonsAndCitiesSearch>['onChange'] = async (value) => {
		if (value?.extra?.type === SEARCH_FIELD_OPTION_TYPE.SALON) {
			// for the salon, there is a redirect to the salon detail handled inside the SalonsAndCitiesSearch component
			const event = getSalonSelectedFromSearchEvent({ inputValue: value?.value ?? '' })
			pushToDataLayer(event)
			setSearchValue(value)
			return
		}

		// SEARCH_FIELD_OPTION_TYPE.CITY
		// clear
		if (!value) {
			if (citiesSeoSlugData && currentCategory?.slug) {
				// clear for city specific page for specific category
				// we want to return user to the category page, so we need to do a redirect
				window.location.href = PAGE_LINKS['/salons/all/:categorySlug'](locale, currentCategory.slug, {
					...query,
					latMy: myLocation?.latMy,
					lonMy: myLocation?.lonMy,
					lat: undefined,
					lon: undefined,
					googlePlaceID: undefined
				})
			} else {
				// otherwise, just clear the city filter
				// we stay on the same page and just refetch the data
				loadSalonsFromFilters({ city: null })
			}

			setSearchValue(null)
			return
		}

		// new value
		if (value?.extra?.type === SEARCH_FIELD_OPTION_TYPE.CITY) {
			// for the city, we either redirect to the city specific page or we stay on the same page and just refetch the data
			const event = getPlaceSelectedEvent({ inputValue: value?.value ?? '' })
			pushToDataLayer(event)

			const MY_LOCATION_OPTION = GET_MY_LOCATION_OPTION()
			const isUsingMyLocation = value.value === MY_LOCATION_OPTION.value

			const filtersCity: LoadSalonsFromFiltersParams['city'] = {
				name: value.label,
				placeID: value.value,
				latitude: value.extra?.latitude,
				longitude: value.extra?.longitude
			}

			if (currentCategory?.slug) {
				// category specific page
				// get existing city specific pages
				const primaryCitySlug = citiesByCategory?.cities?.find((city) => city.city.placeID === value.value)?.slugs?.[0]?.slugValue

				if (primaryCitySlug) {
					// if we have existing city specific page, we redirect to it
					const redirectQuery = { ...query, googlePlaceID: undefined, lat: undefined, lon: undefined }
					window.location.href = PAGE_LINKS['/salons/all/:categorySlug/:citySlug'](locale, currentCategory.slug, primaryCitySlug, redirectQuery)
				} else if (citiesSeoSlugData) {
					// if we don't have existing city specific page for the current selected option,
					// but we are on a city specific page,
					// we redirect to the category page and set the new city to the query parameters
					window.location.href = PAGE_LINKS['/salons/all/:categorySlug'](locale, currentCategory.slug, {
						...query,
						latMy: isUsingMyLocation ? (myLocation?.latMy ?? value.extra?.latitude) : myLocation?.latMy,
						lonMy: isUsingMyLocation ? (myLocation?.lonMy ?? value.extra?.longitude) : myLocation?.lonMy,
						googlePlaceID: isUsingMyLocation ? undefined : value.value,
						lat: isUsingMyLocation ? undefined : value.extra?.latitude,
						lon: isUsingMyLocation ? undefined : value.extra?.longitude
					})
				} else {
					// we are on a category page, but we don't have existing city specific page for the current selected option,
					// so we just set the new city filter and refetch the data
					loadSalonsFromFilters({ city: filtersCity })
				}
			} else {
				// for salons list page, we do not support city specific pages,
				// so we just set the new city filter and refetch the data
				loadSalonsFromFilters({ city: filtersCity })
			}
		}

		setSearchValue(value)
	}

	const onDropdownVisibleChange: ComponentProps<typeof SalonsAndCitiesSearch>['onDropdownVisibleChange'] = (open: boolean) => {
		if (open) {
			const event = getSearchFocusedEvent({ isMobile })
			pushToDataLayer(event)
		}
	}

	return (
		<SC.SalonsSearchWrapper>
			<SalonsAndCitiesSearch
				label={messages['Search for a city or a salon']}
				labelScreenReaderOnly
				value={searchValue}
				emptyContent={{
					emptyTitle: messages['Search salon or city'],
					emptyLabel: messages['Enter the name of the salon or city you are looking for.']
				}}
				showMyLocationOption
				onChange={handleChange}
				onDropdownVisibleChange={onDropdownVisibleChange}
				redirectQueryData={{
					currentCategoryID: currentCategory?.id,
					avResTimeSlotDayPart: query.avResTimeSlotDayPart,
					avResTimeSlotDateFrom: query.avResTimeSlotDateFrom
				}}
				isLoadingValue={isLoadingSearchValue || isLoadingCitiesByCategory}
			/>
		</SC.SalonsSearchWrapper>
	)
}

export default SalonsAndCitiesSearchWrap
